import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FaqSection from "@/components/FaqSection";
import { api, API } from "@/lib/api";

export function blogCoverUrl(a) {
  if (!a) return null;
  if (a.cover_external_url) return a.cover_external_url;
  if (a.cover_path) return `${API}/blogs/media/${a.cover_path}`;
  return null;
}

function ArticleCard({ a }) {
  const cover = blogCoverUrl(a);
  return (
    <Link
      to={`/learn-more/${a.slug}`}
      data-testid={`blog-card-${a.slug}`}
      className="group flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:border-teal-500 dark:hover:border-teal-500/60 hover:shadow-lg transition-all"
    >
      <div className="aspect-[16/9] w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        {cover ? (
          <img
            src={cover}
            alt={a.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <BookOpen className="h-8 w-8 text-slate-300 dark:text-slate-600" />
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50 tracking-tight group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors">
          {a.title}
        </h3>
        {a.excerpt && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-3">
            {a.excerpt}
          </p>
        )}
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 dark:text-teal-400">
          Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

export default function LearnMore() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blogs")
      .then(({ data }) => setArticles(data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col" data-testid="learn-more-page">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-16 sm:py-20">
        {/* Hero */}
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Learn more</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
            Resilience, in plain English
          </h1>
          <p className="mt-4 text-base text-slate-600 dark:text-slate-300 leading-relaxed">
            Practical guides on business continuity and operational resilience for solopreneurs,
            SMEs, and corporate teams — plus answers to the questions we hear most.
          </p>
        </div>

        {/* Blog / insights */}
        <section className="mt-14" data-testid="blog-list">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
              Insights &amp; guides
            </h2>
          </div>

          {loading ? (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-pulse">
                  <div className="aspect-[16/9] bg-slate-100 dark:bg-slate-800" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded" />
                    <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length ? (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a) => <ArticleCard key={a.id} a={a} />)}
            </div>
          ) : (
            <p className="mt-8 text-sm text-slate-500 dark:text-slate-400">No articles published yet — check back soon.</p>
          )}
        </section>

        {/* FAQ */}
        <section className="mt-24" data-testid="learn-more-faq">
          <FaqSection heading="Frequently asked questions" />
        </section>
      </main>
      <Footer />
    </div>
  );
}
